// Documents API
import api, { type AxiosResponse } from './client';
import type {
  DocumentCategory,
  Document,
  DocumentDownload,
  DocumentStats,
  CategoryWithDocuments,
  QueryParams,
  PendingCompanyConfirmation,
  CompanyConfirmationReport,
  DocumentArchiveState,
  BulkArchiveResult,
} from './types';

export const documentsAPI = {
  // Categories
  getCategories: (params?: QueryParams): Promise<AxiosResponse<DocumentCategory[]>> =>
    api.get('/documents/categories/', { params }),

  // Fetch EVERY folder across all pages. The list endpoint paginates
  // (default page_size 20, server max 100), so any caller that builds a
  // folder picker MUST walk all pages or it silently drops folders beyond
  // the first page — a tenant can have hundreds of folders (one per part).
  // The page cap guards against a non-terminating response.
  getAllCategories: async (params?: QueryParams): Promise<DocumentCategory[]> => {
    const all: DocumentCategory[] = [];
    const MAX_PAGES = 50;
    for (let page = 1; page <= MAX_PAGES; page += 1) {
      const res = await api.get('/documents/categories/', {
        params: { ...params, page, page_size: 100 },
      });
      const data = res.data as unknown;
      if (Array.isArray(data)) {
        all.push(...(data as DocumentCategory[]));
        break;
      }
      const paged = data as { results?: DocumentCategory[]; next?: string | null };
      const pageItems = paged.results || [];
      all.push(...pageItems);
      if (!paged.next || pageItems.length === 0) break;
    }
    return all;
  },

  /** Every document matching `params`, walking all pages.
   *  A bare getDocuments() returns only the first page (DRF PAGE_SIZE=20,
   *  max_page_size=100), so any caller that renders a complete list — or
   *  derives something from it, like the supplier tab's folder picker — must
   *  page through rather than trusting one response. Mirrors getAllCategories. */
  getAllDocuments: async (params?: QueryParams): Promise<Document[]> => {
    const all: Document[] = [];
    const MAX_PAGES = 50;
    for (let page = 1; page <= MAX_PAGES; page += 1) {
      const res = await api.get('/documents/documents/', {
        params: { ...params, page, page_size: 100 },
      });
      const data = res.data as unknown;
      if (Array.isArray(data)) {
        all.push(...(data as Document[]));
        break;
      }
      const paged = data as { results?: Document[]; next?: string | null };
      const pageItems = paged.results || [];
      all.push(...pageItems);
      if (!paged.next || pageItems.length === 0) break;
    }
    return all;
  },

  getCategory: (id: number): Promise<AxiosResponse<DocumentCategory>> =>
    api.get(`/documents/categories/${id}/`),

  createCategory: (data: Partial<DocumentCategory>): Promise<AxiosResponse<DocumentCategory>> =>
    api.post('/documents/categories/', data),

  updateCategory: (id: number, data: Partial<DocumentCategory>): Promise<AxiosResponse<DocumentCategory>> =>
    api.patch(`/documents/categories/${id}/`, data),

  deleteCategory: (
    id: number,
    options?: { cascade?: boolean; reassignTo?: number | null },
  ): Promise<AxiosResponse<void>> => {
    // CR-5379: two-mode folder delete. Send cascade / reassign_to as
    // query params so the backend's destroy override can read them
    // without us having to hand-roll a custom request body for an
    // HTTP DELETE.
    const params: Record<string, string> = {};
    if (options?.cascade) params['cascade'] = 'true';
    if (!options?.cascade && options?.reassignTo != null) {
      params['reassign_to'] = String(options.reassignTo);
    }
    return api.delete(`/documents/categories/${id}/`, { params });
  },

  getCategoryDocuments: (id: number): Promise<AxiosResponse<Document[]>> =>
    api.get(`/documents/categories/${id}/documents/`),

  // Documents
  getDocuments: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: Document[] }>> =>
    api.get('/documents/documents/', { params }),

  getDocument: (id: number): Promise<AxiosResponse<Document>> =>
    api.get(`/documents/documents/${id}/`),

  createDocument: (data: FormData): Promise<AxiosResponse<Document>> =>
    api.post('/documents/documents/', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  updateDocument: (id: number, data: FormData | Partial<Document>): Promise<AxiosResponse<Document>> =>
    api.patch(`/documents/documents/${id}/`, data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {}
    }),

  deleteDocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/documents/documents/${id}/`),

  // Archive: hidden from the folder and list views, kept and restorable.
  // List archived documents with getDocuments({ archived: 'true' }).
  archiveDocument: (id: number): Promise<AxiosResponse<DocumentArchiveState>> =>
    api.post(`/documents/documents/${id}/archive/`),

  unarchiveDocument: (id: number): Promise<AxiosResponse<DocumentArchiveState>> =>
    api.post(`/documents/documents/${id}/unarchive/`),

  bulkArchiveDocuments: (ids: number[]): Promise<AxiosResponse<BulkArchiveResult>> =>
    api.post('/documents/documents/bulk_archive/', { ids }),

  downloadDocument: (id: number): Promise<AxiosResponse<{ file_url: string; title: string; file_size: string }>> =>
    api.post(`/documents/documents/${id}/download/`),

  // CR-5331: getPreviewUrl removed. The previous implementation embedded
  // the permanent JWT in the URL (CWE-200) AND was rejected by the
  // backend's preview endpoint (which only honours u/exp/sig signed
  // params). Use downloadDocument(id) to mint a short-lived signed link
  // and open response.data.file_url instead.

  getDocumentsByCategory: (params?: QueryParams): Promise<AxiosResponse<CategoryWithDocuments[]>> =>
    api.get('/documents/documents/by_category/', { params }),

  getDocumentStats: (params?: QueryParams): Promise<AxiosResponse<DocumentStats>> =>
    api.get('/documents/documents/stats/', { params }),

  // CR-5330: Audit history feed for the document details popup. The
  // backend's history action combines the upload event, downloads and
  // read-receipts into a single Changelog-shaped feed (newest first).
  getDocumentHistory: (id: number): Promise<AxiosResponse<DocumentHistoryEntry[]>> =>
    api.get(`/documents/documents/${id}/history/`),

  // CR-5328: Version-history endpoints. The current file lives on
  // Document; older snapshots live in DocumentVersion. uploadNewVersion
  // snapshots the previous file then replaces with the new one.
  getDocumentVersions: (id: number): Promise<AxiosResponse<DocumentVersionEntry[]>> =>
    api.get(`/documents/documents/${id}/versions/`),

  uploadNewVersion: (id: number, data: FormData): Promise<AxiosResponse<Document>> =>
    api.post(`/documents/documents/${id}/upload_version/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  downloadVersion: (id: number, versionId: number): Promise<AxiosResponse<{ file_url: string; title: string; version_number: string; file_size: string }>> =>
    api.post(`/documents/documents/${id}/versions/${versionId}/download/`),

  // CR-5329: Record a Read & Accept confirmation. Idempotent —
  // re-clicking is a no-op. Returns the refreshed Document with
  // has_accepted=true / accepted_at populated for the caller.
  acceptDocument: (id: number): Promise<AxiosResponse<Document>> =>
    api.post(`/documents/documents/${id}/accept/`),

  // CR-5858: company-level "Read & Understood" confirmation. confirmCompany
  // records (or refreshes) a confirmation by the active supplier company.
  // getPendingCompanyConfirmations drives the global supplier blocking modal.
  // getCompanyConfirmationReport returns the customer-side per-company
  // confirmation status for a single document.
  confirmCompany: (id: number): Promise<AxiosResponse<Document>> =>
    api.post(`/documents/documents/${id}/company_confirm/`),

  getPendingCompanyConfirmations: (): Promise<AxiosResponse<PendingCompanyConfirmation[]>> =>
    api.get(`/documents/documents/pending_company_confirmations/`),

  getCompanyConfirmationReport: (id: number): Promise<AxiosResponse<CompanyConfirmationReport>> =>
    api.get(`/documents/documents/${id}/company_confirmation_report/`),

  // Downloads
  getDownloads: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: DocumentDownload[] }>> =>
    api.get('/documents/downloads/', { params }),
};

// CR-5330: Shape returned by the documents history endpoint. Mirrors the
// ChangelogEntry interface in components/common/Changelog.tsx (with id
// loosened to string|number — this endpoint synthesises ids like
// 'upload-12' / 'download-44' from heterogeneous tables).
export interface DocumentHistoryEntry {
  id: string | number;
  change_date: string;
  changed_by: string;
  changed_by_company?: string | null;
  field_changed: string;
  old_value: string | null;
  new_value: string;
  event_type?: string;
}

// CR-5328: Shape returned by the version-history endpoint. Mirrors the
// DocumentVersionSerializer fields.
export interface DocumentVersionEntry {
  id: number;
  document: number;
  version_number: string;
  notes: string;
  uploaded_by: number | null;
  uploaded_by_name: string;
  uploaded_at: string;
  formatted_file_size: string;
  file_extension: string;
  mime_type: string;
}
